import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, ArrowLeft, Copy, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { markLeadDismissed, markLeadSubmitted } from "@/hooks/useLeadCaptureTrigger";

const EASE = [0.16, 1, 0.3, 1];
const API = process.env.REACT_APP_BACKEND_URL;

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (v) => emailRe.test((v || "").trim());
const isValidPhone = (v) => (v || "").replace(/\D/g, "").length >= 7;

/**
 * LeadCapture — bottom sheet en móvil, modal center en desktop.
 * Flujo de dos pasos: (1) email o teléfono → (2) nombre + el otro contacto.
 * Guarda parcialmente en el servidor cada paso para no perder leads.
 */
export default function LeadCapture({ open, onOpenChange, source = "homepage_modal" }) {
  const { t, lang } = useLanguage();
  const reduce = useReducedMotion();
  const closeBtnRef = useRef(null);
  const firstFieldRef = useRef(null);

  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(true);
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [issuedCode, setIssuedCode] = useState("WELCOME10");

  // Reset state when the modal opens (fresh session)
  useEffect(() => {
    if (open) {
      setStep(1);
      setError("");
      setSuccess(false);
      setCopied(false);
      // Focus first field after entrance animation
      const id = window.setTimeout(() => firstFieldRef.current?.focus(), 340);
      // Mark modal open globally so triggers won't re-fire
      document.body.setAttribute("data-lead-modal-open", "1");
      return () => {
        window.clearTimeout(id);
        document.body.removeAttribute("data-lead-modal-open");
      };
    }
  }, [open]);

  // Lock body scroll while open — restore previous position on close
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const prev = { position: document.body.style.position, top: document.body.style.top, width: document.body.style.width };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.dataset.scrollLocked = "1";
    return () => {
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.width = prev.width;
      delete document.body.dataset.scrollLocked;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const handleDismiss = () => {
    if (!success) markLeadDismissed();
    onOpenChange(false);
  };

  const submit = async (currentStep) => {
    setError("");
    // Client-side validation for the fields relevant to this step
    if (currentStep === 1) {
      const hasEmail = email && isValidEmail(email);
      const hasPhone = phone && isValidPhone(phone);
      if (!email && !phone) return setError(t.lead.errorContact);
      if (email && !hasEmail) return setError(t.lead.errorEmail);
      if (phone && !hasPhone) return setError(t.lead.errorPhone);
    } else {
      if (!firstName.trim()) return setError(t.lead.errorName);
      // At final step, at least email or phone valid
      const hasEmail = email && isValidEmail(email);
      const hasPhone = phone && isValidPhone(phone);
      if (!hasEmail && !hasPhone) return setError(t.lead.errorContact);
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName || null,
          email: email || null,
          phone: phone || null,
          marketing_consent: consent,
          source,
          step: currentStep,
          locale: lang,
          website, // honeypot
        }),
      });
      if (!res.ok) throw new Error("save-failed");
      const data = await res.json();
      if (currentStep === 1) {
        setStep(2);
      } else {
        setIssuedCode(data.code || "WELCOME10");
        setSuccess(true);
        markLeadSubmitted();
      }
    } catch (_e) {
      setError(t.lead.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(issuedCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (_e) {
      /* clipboard blocked */
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (!open) return <AnimatePresence />;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            data-testid="lead-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: EASE }}
            onClick={handleDismiss}
            className="fixed inset-0 z-[80] bg-black/45 backdrop-blur-sm"
            aria-hidden="true"
          />

          {/* Sheet / Modal container */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="lead-title"
            data-testid="lead-modal"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 32 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.32, ease: EASE }}
            className="fixed inset-x-0 bottom-0 z-[81] flex justify-center sm:inset-0 sm:items-center sm:p-6"
          >
            <div
              className="pointer-events-auto w-full max-w-[560px] rounded-t-3xl bg-white p-6 pt-4 shadow-[0_-20px_60px_-20px_rgba(0,0,0,0.35)] sm:rounded-3xl sm:p-8"
              style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
            >
              {/* Drag handle (mobile only) */}
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[#e5e5e0] sm:hidden" aria-hidden="true" />

              {/* Header */}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-body text-[10px] uppercase tracking-[0.24em] text-[#999]">
                    {success ? "" : step === 1 ? t.lead.eyebrowStep1 : t.lead.eyebrowStep2}
                    {!success && (
                      <span className="ml-2 text-[#c9c9c2]">· {t.lead.stepIndicator(step)}</span>
                    )}
                  </p>
                  <h2
                    id="lead-title"
                    className="mt-1 font-serif-display text-2xl font-medium leading-tight text-[#111] sm:text-3xl"
                  >
                    {success ? t.lead.successTitle : t.lead.title}
                  </h2>
                </div>
                <button
                  ref={closeBtnRef}
                  onClick={handleDismiss}
                  aria-label={t.lead.close}
                  data-testid="lead-close"
                  className="press -m-2 flex h-10 w-10 items-center justify-center rounded-full text-[#666] transition-colors hover:bg-[#f4f4f0] hover:text-[#111]"
                >
                  <X className="h-5 w-5" strokeWidth={1.5} />
                </button>
              </div>

              {/* ---------- SUCCESS STATE ---------- */}
              {success ? (
                <div data-testid="lead-success" className="mt-6">
                  <p className="font-body text-sm text-[#666]">{t.lead.successSubtitle}</p>
                  <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-[#111] bg-[#fafaf8] p-4">
                    <span
                      data-testid="lead-code"
                      className="font-mono text-xl font-semibold tracking-[0.28em] text-[#111]"
                    >
                      {issuedCode}
                    </span>
                    <button
                      onClick={handleCopy}
                      data-testid="lead-copy"
                      aria-label={t.lead.copy}
                      className="press inline-flex items-center gap-1.5 rounded-full border border-[#111] px-3 py-1.5 font-body text-xs font-medium text-[#111] transition-colors hover:bg-[#111] hover:text-white"
                    >
                      {copied ? (
                        <>
                          <Check className="h-3.5 w-3.5" strokeWidth={1.8} />
                          {t.lead.copied}
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" strokeWidth={1.8} />
                          {t.lead.copy}
                        </>
                      )}
                    </button>
                  </div>
                  <p className="mt-4 font-body text-xs text-[#888]">{t.lead.successNote}</p>
                  <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <a
                      href="#collection"
                      onClick={() => onOpenChange(false)}
                      data-testid="lead-shop-cta"
                      className="press inline-flex items-center justify-center rounded-full bg-[#111] px-5 py-3 font-body text-sm font-medium text-white transition-colors hover:bg-black"
                    >
                      {t.lead.shopCta}
                    </a>
                    <button
                      onClick={() => onOpenChange(false)}
                      className="press inline-flex items-center justify-center rounded-full border border-[#e2e2e0] bg-white px-5 py-3 font-body text-sm text-[#333] transition-colors hover:border-[#111]"
                    >
                      {t.lead.continueCta}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* ---------- FORM STATE ---------- */}
                  <p className="mt-3 font-body text-sm leading-relaxed text-[#666]">
                    {step === 1 ? t.lead.step1Question : t.lead.step2Question}
                  </p>

                  <form
                    className="mt-5 space-y-4"
                    onSubmit={(e) => {
                      e.preventDefault();
                      submit(step);
                    }}
                  >
                    {step === 1 ? (
                      <>
                        <Field
                          testId="lead-email"
                          label={t.lead.emailLabel}
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          placeholder={t.lead.emailPlaceholder}
                          value={email}
                          onChange={setEmail}
                          fieldRef={firstFieldRef}
                        />
                        <div className="text-center font-body text-[10px] uppercase tracking-[0.24em] text-[#bbb]">
                          {lang === "es" ? "o" : "or"}
                        </div>
                        <Field
                          testId="lead-phone"
                          label={t.lead.phoneLabel}
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          placeholder={t.lead.phonePlaceholder}
                          value={phone}
                          onChange={setPhone}
                        />
                      </>
                    ) : (
                      <>
                        <Field
                          testId="lead-firstname"
                          label={t.lead.firstNameLabel}
                          type="text"
                          autoComplete="given-name"
                          placeholder={t.lead.firstNamePlaceholder}
                          value={firstName}
                          onChange={setFirstName}
                          fieldRef={firstFieldRef}
                        />
                        {/* Show the OTHER contact method */}
                        {isValidEmail(email) && !isValidPhone(phone) && (
                          <Field
                            testId="lead-phone"
                            label={`${t.lead.phoneLabel} (${lang === "es" ? "opcional" : "optional"})`}
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            placeholder={t.lead.phonePlaceholder}
                            value={phone}
                            onChange={setPhone}
                          />
                        )}
                        {isValidPhone(phone) && !isValidEmail(email) && (
                          <Field
                            testId="lead-email"
                            label={t.lead.emailLabel}
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            placeholder={t.lead.emailPlaceholder}
                            value={email}
                            onChange={setEmail}
                          />
                        )}
                      </>
                    )}

                    {/* Honeypot — hidden from real users, bots often fill it */}
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
                    />

                    {error && (
                      <p
                        data-testid="lead-error"
                        role="alert"
                        className="rounded-lg bg-[#fff4f2] px-3 py-2 font-body text-xs text-[#b3261e]"
                      >
                        {error}
                      </p>
                    )}

                    <div className="pt-1">
                      {step === 2 && (
                        <button
                          type="button"
                          onClick={() => {
                            setError("");
                            setStep(1);
                          }}
                          className="press mb-3 inline-flex items-center gap-1 font-body text-xs text-[#666] hover:text-[#111]"
                        >
                          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} />
                          {t.lead.back}
                        </button>
                      )}
                      <button
                        type="submit"
                        data-testid={step === 1 ? "lead-continue" : "lead-submit"}
                        disabled={submitting}
                        aria-busy={submitting}
                        className="press inline-flex w-full items-center justify-center rounded-full bg-[#111] px-6 py-3.5 font-body text-sm font-medium text-white transition-colors hover:bg-black disabled:opacity-60"
                      >
                        {submitting
                          ? (lang === "es" ? "Enviando…" : "Sending…")
                          : step === 1
                            ? t.lead.ctaStep1
                            : t.lead.ctaStep2}
                      </button>
                    </div>

                    <p className="pt-2 font-body text-[11px] leading-relaxed text-[#999]">
                      {t.lead.consent}
                    </p>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// -----------------------------------------------------------------------------
// Field — reusable premium input (16px on mobile to prevent iOS zoom)
// -----------------------------------------------------------------------------
function Field({ testId, label, value, onChange, fieldRef, ...inputProps }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-body text-[11px] font-medium uppercase tracking-wider text-[#666]">
        {label}
      </span>
      <input
        ref={fieldRef}
        data-testid={testId}
        {...inputProps}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full rounded-xl border border-[#e2e2e0] bg-white px-4 py-3 text-base text-[#111] transition-colors placeholder:text-[#bbb] focus:border-[#111] focus:outline-none focus:ring-2 focus:ring-[#111]/8"
        style={{ fontSize: "16px" }} /* prevent iOS zoom */
      />
    </label>
  );
}
